import { useEffect, useState } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { noticeForError } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/noticeForError';
import { WriteDialog } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/WriteDialog';
import { SEAT_KIND, useGetSeatDetailQuery } from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

import { Button, Typography } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';

import { knownSeats, revocableSeats, type SeatHolder } from './memberSeats';
import { attachmentConsequence, membershipConsequence } from './seatConsequence';
import { SeatConsequence, SeatPicker } from './SeatDialogParts';

const { seatActions } = appStrings.modules.coachingDashboard;
const strings = seatActions.revokeDialog;

export type RevokeDoors = Readonly<{
  isRevoking: boolean;
  onRevoke: (args: {
    rosterId: number;
    seatId: number;
    name: string;
  }) => Promise<{ ok: boolean; message: string | null }>;
}>;

type RevokeSeatDialogViewProps = Readonly<{
  open: boolean;
  onClose: () => void;
  holder: SeatHolder;
  /** Coach seats end with a different consequence: the cohorts they coach, not join. */
  isCoachSeat: boolean;
  doors: RevokeDoors;
}>;

export const RevokeSeatDialogView = ({
  open,
  onClose,
  holder,
  isCoachSeat,
  doors,
}: RevokeSeatDialogViewProps) => {
  const seats = revocableSeats(
    knownSeats(holder.seats),
    isCoachSeat ? SEAT_KIND.coach : SEAT_KIND.student,
  );
  const [seatId, setSeatId] = useState<number | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const seat = seats.find((row) => row.seat_id === seatId) ?? seats[0];

  useEffect(() => {
    if (!open) return;

    setSeatId(null);
    setRefusal(null);
  }, [open]);

  const detail = useGetSeatDetailQuery(
    open && seat !== undefined ? { rosterId: seat.roster_id, seatId: seat.seat_id } : skipToken,
  );
  const consequence = isCoachSeat
    ? attachmentConsequence(detail.data, detail.isFetching, detail.isError)
    : membershipConsequence(detail.data, detail.isFetching, detail.isError);

  const submit = async () => {
    if (seat === undefined) return;

    setRefusal(null);

    const outcome = await doors.onRevoke({
      rosterId: seat.roster_id,
      seatId: seat.seat_id,
      name: holder.name,
    });

    if (outcome.ok) {
      onClose();

      return;
    }

    setRefusal(noticeForError(outcome.message, strings.failed));
  };

  return (
    <WriteDialog
      id="coaching-dashboard-revoke-seat-dialog"
      open={open}
      onClose={onClose}
      title={strings.title(holder.name)}
      footer={
        <>
          <Button
            id="coaching-dashboard-revoke-seat-cancel"
            hierarchy="Secondary gray"
            size="md"
            onClick={onClose}
          >
            {seatActions.cancel}
          </Button>
          <Button
            id="coaching-dashboard-revoke-seat-submit"
            hierarchy="Primary destructive"
            size="md"
            disabled={seat === undefined || doors.isRevoking}
            onClick={() => void submit()}
          >
            {doors.isRevoking ? strings.submitting : strings.submit}
          </Button>
        </>
      }
    >
      {refusal !== null ? (
        <Typography
          id="coaching-dashboard-revoke-seat-refusal"
          variant="text-sm-regular"
          role="alert"
        >
          {refusal}
        </Typography>
      ) : null}
      <SeatPicker
        id="coaching-dashboard-revoke-seat-picker"
        seats={seats}
        selectedSeatId={seat?.seat_id ?? null}
        onChange={setSeatId}
      />
      <SeatConsequence
        id="coaching-dashboard-revoke-seat-consequence"
        source={consequence}
        isCoachSeat={isCoachSeat}
      />
    </WriteDialog>
  );
};
